import axiosInstanceApiGateway from 'config/apiServiceApiGateway'
import type { BlacklistSummaryType } from 'features/Location/@types/GreylistArea/List'
import type {
  BlackListDetailType,
  DurationType,
} from 'features/Location/@types/GreylistArea/AddEdit'

const MULTIPART_FORM_DATA = 'multipart/form-data'

const { Get, Post, Delete, Put } = axiosInstanceApiGateway

export type GetBlacklistAreasRequestType = {
  params: { limit: number; page: number; location_name: string }
}

export type GetBlacklistAreasResponseType = {
  blacklist_address_summary: BlacklistSummaryType[]
}

export const getBlacklistAreas = ({ params }: GetBlacklistAreasRequestType) =>
  Get<GetBlacklistAreasResponseType>({
    url: `/location/internal/v2/blacklist-address-summary`,
    params,
  })

export type GetRestrictedlistAreasResponseType = {
  restricted_address_summary: BlacklistSummaryType[]
}

export const getRestrictedlistAreas = ({ params }: GetBlacklistAreasRequestType) =>
  Get<GetRestrictedlistAreasResponseType>({
    url: `/location/internal/v1/restricted-address-summary`,
    params,
  })

export type GetBlacklistAreaDetailRequestType = {
  location_id: string
}

export type GetBlacklistAreaDetailResponseType = BlackListDetailType[]

export const getBlacklistAreaDetail = ({ location_id }: GetBlacklistAreaDetailRequestType) =>
  Get<GetBlacklistAreaDetailResponseType>({
    url: `/location/internal/v2/blacklist-address/${location_id}/detail`,
  })
export const getRestrictedlistAreaDetail = ({ location_id }: GetBlacklistAreaDetailRequestType) =>
  Get<GetBlacklistAreaDetailResponseType>({
    url: `/location/internal/v1/restricted-address/${location_id}/detail`,
  })

export type PostCreateBlacklistAddressBulkRequestType = {
  blacklist_address: {
    location: {
      id: number
      name: string
      type: string
    }
    address: string
    type: number
    duration_type: string
    is_active: boolean
    start_date: string
    end_date: string
    file: string
  }[]
}

export type PostCreateRestrictedlistAddressBulkRequestType = {
  restricted_address: {
    location: {
      id: number
      name: string
      type: string
    }
    address: string
    file: string
    type: string
    address_type: number
    duration_type: DurationType
    start_date: string
    end_date: string
    is_active: boolean
    close_reason_id: number
    service_level_id: number
    total_user: number
  }[]
}

export type PostCreateBlacklistAddressBulkResponseType = {
  message: string
}

export const postCreateBlacklistAddressBulk = ({
  blacklist_address,
}: PostCreateBlacklistAddressBulkRequestType) =>
  Post<PostCreateBlacklistAddressBulkResponseType>({
    url: `/location/internal/v2/blacklist-address-bulk`,
    data: { blacklist_address },
  })

export const postCreateRestrictedlistAddressBulk = ({
  restricted_address,
}: PostCreateRestrictedlistAddressBulkRequestType) =>
  Post<PostCreateBlacklistAddressBulkResponseType>({
    url: `/location/internal/v1/restricted-address-bulk`,
    data: { restricted_address },
  })

export type DeleteBlacklistAddressRequestType = {
  id: string
}
export type DeleteBlacklistAddressResponseType = {
  message: string
}

export const deleteBlacklistAddress = ({ id }: DeleteBlacklistAddressRequestType) =>
  Delete<DeleteBlacklistAddressResponseType>({
    url: `/location/internal/v2/blacklist-address/${id}`,
  })

export const deleteRestrictedlistAddress = ({ id }: DeleteBlacklistAddressRequestType) =>
  Delete<DeleteBlacklistAddressResponseType>({
    url: `/location/internal/v1/restricted-address/${id}`,
  })

export type PutBlacklistAddressRequestType = {
  id: string
  data: {
    location: {
      id: number
      name: string
      type: string
    }
    address: string
    type: number
    duration_type: string
    start_date: string
    end_date: string
    is_active: boolean
    file: string
  }
}

export const putBlacklistAddress = ({ id, data }: PutBlacklistAddressRequestType) =>
  Put<DeleteBlacklistAddressResponseType>({
    url: `/location/internal/v2/blacklist-address/${id}`,
    data,
  })

export type DeleteBlacklistAddressByLocationIDRequestType = {
  id: string
}

export type DeleteBlacklistAddressByLocationIDResponseType = {
  message: string
}

export const deleteBlacklistAddressByLocationID = ({
  id,
}: DeleteBlacklistAddressByLocationIDRequestType) =>
  Delete<DeleteBlacklistAddressByLocationIDResponseType>({
    url: `/location/internal/v2/blacklist-address/${id}/detail`,
  })

export type DeleteRestrictedlistAddressByLocationIDRequestType = {
  location_id: string
}
export const deleteRestrictedlistAddressByLocationID = ({
  location_id,
}: DeleteRestrictedlistAddressByLocationIDRequestType) =>
  Delete<DeleteBlacklistAddressByLocationIDResponseType>({
    url: `/location/internal/v1/restricted-address/${location_id}/detail`,
  })

export type PostBulkUploadCSVBlacklistAreaRequestType = { data: FormData }
export type PostBulkUploadCSVBlacklistAreaResponseType = {
  failed_data: number
  status: string
  success_data: number
  total_data: number
}

export const postBulkUploadCSVBlacklistArea = ({
  data,
}: PostBulkUploadCSVBlacklistAreaRequestType) =>
  Post<PostBulkUploadCSVBlacklistAreaResponseType>({
    url: `/location/internal/v2/blacklist-address/bulk-upload`,
    data,
    config: {
      headers: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'Content-Type': MULTIPART_FORM_DATA,
      },
    },
  })

export const postBulkUploadIsochroneBlacklistArea = ({
  data,
}: PostBulkUploadCSVBlacklistAreaRequestType) =>
  Post<PostBulkUploadCSVBlacklistAreaResponseType>({
    url: `/location/internal/v1/isochrone/blacklist-address/bulk-upload`,
    data,
    config: {
      headers: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'Content-Type': MULTIPART_FORM_DATA,
      },
    },
  })

export const postBulkUploadCSVRestrictedlistArea = ({
  data,
}: PostBulkUploadCSVBlacklistAreaRequestType) =>
  Post<PostBulkUploadCSVBlacklistAreaResponseType>({
    url: `/location/internal/v1/restricted-address/bulk-upload`,
    data,
    config: {
      headers: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'Content-Type': MULTIPART_FORM_DATA,
      },
    },
  })

export const postBulkUploadIsochroneRestrictedlistArea = ({
  data,
}: PostBulkUploadCSVBlacklistAreaRequestType) =>
  Post<PostBulkUploadCSVBlacklistAreaResponseType>({
    url: `/location/internal/v1/location-geometry/restricted-address/bulk-upload`,
    data,
    config: {
      headers: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'Content-Type': MULTIPART_FORM_DATA,
      },
    },
  })

export type TemplateType =
  | 'template-create-blacklist-isochrone'
  | 'template-update-blacklist-isochrone'
  | 'template-upload-blacklist-address'
  | 'template-upload-blacklist-isochrone'
  | 'template-create-restricted-isochrone'
  | 'template-update-restricted-isochrone'
  | 'template-upload-restricted-address'
  | 'template-upload-restricted-isochrone'

export type GetBlacklistTemplateRequestType = {
  type: TemplateType
}

export type GetBlacklistTemplateResponseType = {
  filename: string
  file_byte: string
}

export const getBlacklistTemplate = ({ type }: GetBlacklistTemplateRequestType) =>
  Get<GetBlacklistTemplateResponseType>({
    url: `/location/internal/v1/restricted-address/download-template/${type}`,
  })

export type UpdateBlacklistAddressType = {
  id: string
  location: {
    id: number
    name: string
    type: string
  }
  address: string
  file: string
  type: number
  duration_type: string
  start_date: string
  end_date: string
  is_active: boolean
}

export type PutUpdateBlacklistAddressBulkRequestType = {
  payload: {
    blacklist_address: UpdateBlacklistAddressType[]
  }
  locationId: string
}

export const putUpdateBlacklistAddressBulk = ({
  payload,
  locationId,
}: PutUpdateBlacklistAddressBulkRequestType) =>
  Put<{
    message: string
  }>({
    url: `/location/internal/v2/blacklist-address/${locationId}/detail`,
    data: payload,
  })

export type BlacklistPathType = 'blacklist-address' | 'restricted-address'
export type ImpactedUserVersionType = 'v1' | 'v2'

export type PostGetImpactedUserByFileRequestType = {
  data: FormData
  blacklistPathType: BlacklistPathType
  version: ImpactedUserVersionType
}
export type PostGetImpactedUserByAddressRequestType = {
  address: string
  location_id: number
  blacklistPathType: BlacklistPathType
  version: ImpactedUserVersionType
}
export type PostGetImpactedUserResponseType = { total_user: number }

export const postGetImpactedUserByFile = ({
  data,
  blacklistPathType,
  version,
}: PostGetImpactedUserByFileRequestType) =>
  Post<PostGetImpactedUserResponseType>({
    url: `/location/internal/${version}/${blacklistPathType}/total-user`,
    data,
    config: {
      headers: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'Content-Type': MULTIPART_FORM_DATA,
      },
    },
  })

export const postGetImpactedUserByAddress = ({
  address,
  location_id,
  blacklistPathType,
  version,
}: PostGetImpactedUserByAddressRequestType) =>
  Post<PostGetImpactedUserResponseType>({
    url: `/location/internal/${version}/${blacklistPathType}/total-user`,
    data: { address, location_id },
  })

export type UpdateRestrictlistAddressType = {
  id: string
  location: {
    id: number
    name: string
    type: string
  }
  address: string
  file: string
  address_type: number
  duration_type: string
  start_date: string
  end_date: string
  type: string
  is_active: boolean
  close_reason_id: number
  service_level_id: number
  total_user: number
}

export type PutUpdateRestrictlistAddressBulkRequestType = {
  payload: {
    restricted_address: UpdateRestrictlistAddressType[]
  }
  locationId: string
}

export const putUpdateRestrictlistAddressBulk = ({
  payload,
  locationId,
}: PutUpdateRestrictlistAddressBulkRequestType) =>
  Put<{
    message: string
  }>({
    url: `/location/internal/v1/restricted-address/${locationId}/detail`,
    data: payload,
  })
